import BaseService from "./BaseService";
import AppService from "./AppService";
import log from "loglevel";
import MapService from "./MapService";
import i18n from "i18next";

import { Network } from '@capacitor/network';
import LogManager from "./LogManager";
import UrlService from "./UrlService";
import AppLifecycleService from "./AppLifecycleService";
import type { LifecycleHandler } from "./AppLifecycleService";

/**
 * Helper for App network management.
 */
class NetworkService extends BaseService {
    static offline = false;

    static async init(): Promise<void> {
        this.debug("NetworkService.init()");

        if (AppService.isPwa()) {
            await this.initPWA();
        } else  {
            await this.initStandAlone();
        }
    }

    /**
     * Detects if the device has netowrk
     *
     */
    static isOffline(): boolean {
        return this.offline;
    }

    static appActiveHandler: LifecycleHandler = () => {
        this.checkNetworkStatus();
    }

    /**
     * Registers the handler that re-checks connectivity when the app returns
     * to the foreground.
     */
    static initAppActiveHandler(): void {
        AppLifecycleService.addAppActiveHandler(this.appActiveHandler);
    }

    /**
     * Re-queries the current network status and re-runs the offline/online
     * transition through setOffline. While the app is in the background the OS
     * may not deliver network change events, so the offline toast can stay
     * stuck even though connectivity was already restored. Calling this when
     * the app becomes active again forces the reconnect handler to run (and
     * clear the toast) if there is network.
     */
    static async checkNetworkStatus(): Promise<void> {
        const connected = AppService.isPwa()
            ? await this.checkConnectivity()
            : (await Network.getStatus()).connected;
        this.setOffline(!connected);
    }

    /**
     * Actively check whether the REST host is reachable.
     *
     * navigator.onLine has false negatives (with VPNs and some network
     * setups it reports offline while the network works), so before
     * believing "offline" we verify with a real request.
     *
     * @return True if the REST host answered, whatever the HTTP status.
     */
    static async checkConnectivity(): Promise<boolean> {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 4000);
        try {
            // no-cors: the response is opaque, we only care that it arrived.
            // Only a network failure or the timeout rejects.
            await fetch(this.config().rest_url, {
                method: 'HEAD',
                mode: 'no-cors',
                cache: 'no-store',
                signal: controller.signal,
            });
            return true;
        } catch {
            return false;
        } finally {
            clearTimeout(timer);
        }
    }

    static async initPWA(): Promise<void> {
        // control net status
        window.addEventListener("offline", () => {
            // The browser flag has false negatives: verify before believing.
            this.checkNetworkStatus();
        }, false);
        window.addEventListener("online", () => {
            this.setOffline(false);
        }, false);

        // Initialize with the current status: the events above only fire on
        // changes, so if the browser is already offline at startup 'offline'
        // would keep its optimistic default value.
        if (window.navigator.onLine) {
            this.setOffline(false);
        } else {
            await this.checkNetworkStatus();
        }
    }

    static async initStandAlone(): Promise<void> {
        Network.addListener('networkStatusChange', status => {
            this.setOffline(!status.connected);
        });
        const status = await Network.getStatus();
        this.setOffline(!status.connected);
    }

    static setOffline(value: boolean): void {
        if (!value) {
            log.info('We have network connection');
            if (this.offline === true) {
                if (!MapService.mapLoaded && AppService.needsUserLogin()) {
                    setTimeout(() => {
                        UrlService.reloadWindow("OfflineContent.constructor, no mapLoaded.");
                    }, 100);
                } else {
                    // Sin login no se recarga aunque el mapa no haya cargado:
                    // el token anónimo lo reintenta SiteAuth y la carga del
                    // mapa, MapErrorService. Recargar solo perdería el mapa, y
                    // si justo tras reconectar falla el token, la app se
                    // quedaría sin él (#673).
                    //
                    // Si hay una card offline abierta, reproducir el click para
                    // abrir la card real; también con el mapa cargando, porque
                    // setCardFromPlaceData ya espera a que esté listo. En
                    // setTimeout para que this.offline ya valga false y la
                    // petición no se vuelva a bloquear en getCardFromPlaceData.
                    setTimeout(() => {
                        if (MapService.mapComponent) MapService.mapComponent.restoreCardAfterReconnect();
                    }, 0);
                }
                // Limpiar cola antes de cerrar: si el toast offline estaba encolado
                // (no llegó a mostrarse), no debe aparecer al restaurar la conexión.
                LogManager.clearLogs();
                LogManager.unlock();
            }
        } else {
            log.info('No network connection');
            if (this.offline !== true) {
                LogManager.createError(i18n.t('offline.message'), true, true);
                // Cerrar el loader al detectar offline: cualquier petición en curso
                // fallará y el flujo de inicialización no puede completarse normalmente.
                if (AppService.appcomponent) {
                    AppService.setLoadingState(false);
                }
            }
        }
        this.offline = value;
    }

}

export default NetworkService;
